import { useCallback, useEffect } from "react";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { useLocalStorage } from "./useLocalStorage";

const VALID_MODES = new Set(["light", "dark", "system"]);

function resolveSystemPreference() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Manages theme mode (light / dark / system), persists the user's choice,
 * and reflects the resolved theme onto <html data-theme="..."> for CSS.
 */
export function useTheme() {
  const [mode, setMode] = useLocalStorage(
    STORAGE_KEYS.THEME,
    "system",
    (value) => typeof value === "string" && VALID_MODES.has(value)
  );

  useEffect(() => {
    const root = document.documentElement;

    const applyResolved = () => {
      const resolved = mode === "system" ? resolveSystemPreference() : mode;
      root.setAttribute("data-theme", resolved);
    };

    applyResolved();

    if (mode !== "system" || !window.matchMedia) return undefined;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", applyResolved);
    return () => media.removeEventListener("change", applyResolved);
  }, [mode]);

  const cycleMode = useCallback(() => {
    setMode((prev) => {
      if (prev === "light") return "dark";
      if (prev === "dark") return "system";
      return "light";
    });
  }, [setMode]);

  return { mode, setMode, cycleMode };
}
